/**
 * ComposerLiveButton — the composer's entry point into live voice mode.
 *
 * Live used to SHARE the primary slot with send: the sparkle sat there while
 * the field was empty and send took over once you typed. That made the primary
 * action ambiguous and, worse, hid live behind an icon that only appeared in
 * one transient state. It now has its own permanent control beside send — send
 * stays the single brand-filled primary, and live reads as the secondary it is.
 *
 * The visible "Live" word was dropped deliberately: the composer's action row
 * is tight on the phones this ships to, and the shimmering waveform reads as
 * the affordance. What made the ORIGINAL sparkle a problem was that it was
 * transient and nameless — this control is permanent, and its name survives in
 * `aria-label` + tooltip, so assistive tech and the e2e selector are unaffected.
 * The cost is real though: sighted users now learn "live" from the icon and the
 * tooltip rather than from the button itself.
 *
 * Rendered only where the parent passes `onLiveTap`, so a surface opts out of
 * live simply by not wiring the handler (see ChatComposer's prop docs).
 */

import { Box, Button } from '@chakra-ui/react';

import { AiShimmer } from '../../ui/AiShimmer';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { Tooltip } from '../../ui/Tooltip';

interface ComposerLiveButtonProps {
  readonly onLiveTap: () => void;
  readonly isDisabled?: boolean;
}

export function ComposerLiveButton({ onLiveTap, isDisabled = false }: ComposerLiveButtonProps) {
  return (
    // The flex item in the composer's action row is AiShimmer's wrapper Box,
    // not the Button — so the no-shrink has to live out here or it applies to
    // the wrong element and the button survives only on `min-width: auto`.
    <Box flexShrink={0}>
      {/* `edge`, not the default blob: the blob is a blurred radial wash that
        swallows the button's own outline, which on a control this small read as
        a filled purple pill rather than a glowing one. The edge trace is the
        same treatment the dashboard composer wears, so the two AI affordances
        now speak with one voice. `borderRadius` must track the Button's `lg`
        below or the halo sits proud of the corners. */}
      <AiShimmer intensity="default" variant="edge" borderRadius="lg" isActive={!isDisabled}>
        <Tooltip label="Live conversation" placement="top">
          <Button
            // Carries the whole accessible name now that the control is
            // icon-only — this is what screen readers announce and what the
            // e2e BIRD_GO_LIVE selector matches. Do not remove.
            aria-label="Start live conversation"
            onClick={onLiveTap}
            isDisabled={isDisabled}
            size="md"
            // Square: 44×44 is the WCAG 2.5.5 target floor, and with the label
            // gone the button must hold that size on its own rather than
            // inheriting width from text. Live ships ONLY on mobile, where
            // this is a primary touch target.
            h="44px"
            w="44px"
            px={0}
            // The sanctioned quiet-brand treatment — its own comment describes
            // exactly this hierarchy (solid-primary > outline-primary). Hand-
            // rolling `brand.onSurface` on a `brand.muted` fill instead measured
            // 3.98:1 light / 3.69:1 dark, under AA for 16px text: the plain
            // foreground tokens are verified against bg.surface, and fall below
            // AA the moment the background is tinted.
            variant="outline-primary"
            fontWeight="medium"
            borderRadius="lg"
            transition="all 0.2s ease"
          >
            {/* Material Symbols are a ligature font, so this span's text node is
              the literal token "graphic_eq". Harmless while a text label sat
              beside it; on an icon-only button it is the button's only content,
              so hide it and let `aria-label` be the sole name. */}
            <MaterialIcon name="graphic_eq" boxSize={5} color="inherit" aria-hidden />
          </Button>
        </Tooltip>
      </AiShimmer>
    </Box>
  );
}
